import React from "react";
import {
    X,
    Calendar,
    MapPin,
    Clock,
    Info,
    CheckCircle2,
    AlertCircle
} from "lucide-react";

export default function ViewEventModal({ event, isOpen, onClose }) {
    if (!isOpen || !event) return null;

    const getStatusStyle = (status) => {
        switch (status) {
            case 'On Sale':
                return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
            case 'Sold Out':
                return 'bg-black text-white dark:bg-white dark:text-black';
            case 'Presale':
                return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400';
            case 'Coming Soon':
                return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
            case 'Free':
                return 'bg-jazz-brown text-white';
            case 'Completed':
            case 'Done':
                return 'bg-black/5 text-black/40 dark:bg-white/5 dark:text-white/40';
            default:
                return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-2xl max-h-[90vh] rounded-3xl shadow-2xl overflow-y-auto transform transition-all border border-black/5 dark:border-white/5">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white/80 dark:bg-[#1A1A1A]/80 backdrop-blur-md px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
                    <h2 className="text-xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                        Event Overview
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-black hover:text-black/70 dark:text-white/60 dark:hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 sm:p-8 space-y-8">
                    {/* Event Title & Basic Info */}
                    <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <h1 className="text-3xl font-bold text-jazz-brown leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                                {event.title}
                            </h1>
                            <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider self-start ${getStatusStyle(event.status)}`}>
                                {event.status === 'On Sale' ? <CheckCircle2 size={12} /> :
                                    event.status === 'Sold Out' ? <AlertCircle size={12} /> :
                                        <Clock size={12} />}
                                {event.status}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3 text-black dark:text-white/60">
                                <div className="w-10 h-10 rounded-xl bg-jazz-brown/5 flex items-center justify-center text-jazz-brown">
                                    <Calendar size={18} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs font-bold uppercase tracking-widest text-black/60">Date</span>
                                    <span className="text-sm font-medium">{event.day || event.date} {event.month} {event.year}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 text-black dark:text-white/60">
                                <div className="w-10 h-10 rounded-xl bg-jazz-brown/5 flex items-center justify-center text-jazz-brown">
                                    <Clock size={18} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs font-bold uppercase tracking-widest text-black/60">Time</span>
                                    <span className="text-sm font-medium">{event.time}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 text-black dark:text-white/60">
                                <div className="w-10 h-10 rounded-xl bg-jazz-brown/5 flex items-center justify-center text-jazz-brown">
                                    <MapPin size={18} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs font-bold uppercase tracking-widest text-black/60">Venue</span>
                                    <span className="text-sm font-medium">{event.venue}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 text-black dark:text-white/60">
                                <div className="w-10 h-10 rounded-xl bg-jazz-brown/5 flex items-center justify-center text-jazz-brown">
                                    <Info size={18} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs font-bold uppercase tracking-widest text-black/60">Location</span>
                                    <span className="text-sm font-medium">{event.location}</span>
                                </div>
                            </div>

                            {event.ticket_url && (
                                <div className="flex items-center gap-3 text-black dark:text-white/60 sm:col-span-2 pt-2 border-t border-black/5 dark:border-white/5">
                                    <div className="w-10 h-10 rounded-xl bg-jazz-brown/5 flex items-center justify-center text-jazz-brown">
                                        <Info size={18} />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold uppercase tracking-widest text-black/60">Ticket Link</span>
                                        <a
                                            href={event.ticket_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm font-medium text-jazz-brown hover:underline break-all"
                                        >
                                            {event.ticket_url}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    {event.description && (
                        <div className="pt-8 border-t border-black/5 dark:border-white/5">
                            <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-4">Description</h3>
                            <div className="text-black dark:text-white/70 text-base leading-relaxed font-serif italic whitespace-pre-wrap">
                                "{event.description}"
                            </div>
                        </div>
                    )}


                    {/* Footer / Meta */}
                    <div className="pt-8 border-t border-black/5 dark:border-white/5">
                        <div className="flex items-center gap-2 text-xs text-black/60 font-bold uppercase tracking-widest">
                            <Info size={12} />
                            Last updated on: {new Date().toLocaleDateString()}
                        </div>
                    </div>
                </div>

                {/* Sticky Footer */}
                <div className="sticky bottom-0 bg-white dark:bg-[#1A1A1A] p-6 border-t border-black/5 dark:border-white/5">
                    <button
                        onClick={onClose}
                        className="w-full py-4 bg-gray-100 dark:bg-white/5 rounded-xl text-xs font-bold tracking-widest text-black dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-all uppercase"
                    >
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
}
